import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "motion/react"
import { useEffect } from "react"
import { MARCA_SESION, VIAJE_LAMINA } from "@/lib/transiciones"
import { LogoSetieca } from "./LogoSetieca"

/**
 * La apertura de la marca: **«25 · Años · Evolucionando la Logística»**, el logotipo
 * entero revelado por un barrido diagonal, y al final el nombre del producto.
 *
 * Es la secuencia de la **firma corporativa de SETIECA Agentes Aduanales**, portada
 * desde `firma.js` de `setieca_procesos` con su guion de tiempos intacto — los
 * mismos 520 ms de entrada del «25», los mismos 80 ms entre letras de «Años», los
 * mismos 30 entre las de la frase. Esos números son los que hacen que se lea como
 * algo que se escribe y no como un texto que aparece de golpe.
 *
 * ⚠ **No cierra ni vuelve a empezar.** El original es un GIF en bucle: se desvanece
 * a los 6,1 s y arranca de nuevo. Aquí termina en el logotipo y ahí se queda. Un
 * bucle en una pantalla de acceso es un movimiento perpetuo justo al lado de donde
 * alguien teclea su contraseña.
 *
 * ⚠ **El formulario no espera a que termine**: los campos se pueden rellenar desde
 * el primer fotograma. La marca se presenta al lado.
 *
 * ⚠ **Todo se deriva de un solo instante `t`**, igual que el original. Con un
 * temporizador por etapa, cada letra acumularía su propio desfase y la frase se
 * escribiría desigual. Es también lo que deja encadenar el nombre del producto al
 * final del barrido sin tener que medirlo dos veces.
 */

/** Guion de tiempos, en milisegundos. Los del original hasta el barrido. */
const T = {
  /** El «25» entra con un punto de escala, como si se posara. */
  entra25: [0, 520],
  /** «Años», letra a letra. */
  palabra: { ini: 560, paso: 80, dur: 280 },
  /** «Evolucionando la Logística», más rápido: son muchas más letras. */
  frase: { ini: 1280, paso: 30, dur: 200 },
  /** Sale la fila entera para dejar sitio al logotipo. */
  salida: [3030, 3480],
  /** Entra el logotipo y lo revela el barrido. */
  logo: 3530,
  trazo: 1200,
  /** El nombre del producto, encadenado al final del barrido. */
  producto: { ini: 4500, paso: 45, dur: 260 },
  /** El halo dorado del «25»: se posa con él y se va con la fila. */
  halo: [120, 900],
  /** El filete dorado bajo la marca, trazado desde el centro al acabar el barrido. */
  filete: [4700, 5400],
  total: 5600,
}

const PALABRA = "Años"
const FRASE = "Evolucionando la Logística"

/** Ancho del borde difuso del barrido, en fracción del recorrido. */
const SUAVE = 0.18

export function IntroMarca({
  producto,
  className,
}: {
  /** El nombre del producto, escrito letra a letra al terminar el barrido. */
  producto?: string
  className?: string
}) {
  const quieto = useReducedMotion()
  // ⚠ **La línea de tiempo dura lo que tarde la ÚLTIMA letra del producto**, no un total
  // fijo: con «SetiecaDesk Transporte» (25/10) el total de 5,6 s cortaba la animación y la
  // «E» final se quedaba congelada a medio aparecer, más baja y tenue.
  const fin = Math.max(
    T.total,
    producto ? T.producto.ini + producto.length * T.producto.paso + T.producto.dur : 0,
  )
  const t = useMotionValue(quieto ? fin : 0)

  useEffect(() => {
    // Quien pidió al sistema que no le muevan la pantalla ve el logotipo montado,
    // que es el último fotograma. No es «sin animación»: es su final, y sigue
    // diciendo lo mismo.
    if (quieto) return
    const control = animate(t, fin, { duration: fin / 1000, ease: "linear" })
    return () => control.stop()
  }, [quieto, t, fin])

  return (
    <div className={className}>
      {/* ⚠ **Sin pastilla: la marca se pinta DIRECTAMENTE sobre el cristal.** Hubo
          una placa clara detrás, y era la única forma de que el logotipo del manual
          —wordmark en vino casi negro— se viera sobre un panel oscuro. Se quitó
          porque un rectángulo blanco dentro de una lámina de cristal se lee como
          algo pegado encima, no como parte de ella. Lo que la sustituye es una
          versión CLARA del propio logotipo: mismo trazo, misma marca, aclarada en
          proporción a lo oscuro que era cada zona. */}
      <div className="relative h-[132px] w-full overflow-hidden sm:h-[164px]">
        <Veinticinco t={t} />
        <Logotipo t={t} />
      </div>

      {producto && (
        // ⚠ **Wordmark, no un párrafo.** En Inter semibold esto se leía como una línea
        // de texto más justo debajo de un logotipo — es decir, como un pie de foto.
        // Lo que lo convierte en el nombre de un producto son la tipografía de la
        // marca y las mayúsculas.
        //
        // El espaciado se dejó en el NATURAL de la fuente, sin `tracking`: Montserrat
        // ya viene abierta de fábrica, y separarla más hacía que las letras se leyeran
        // sueltas en vez de como una palabra. El peso hace el trabajo que se le pedía
        // al espaciado.
        <p className="mt-1 text-center font-marca text-[0.85rem] font-bold text-[rgb(var(--marco-tinta)/0.95)] uppercase sm:text-[0.95rem]">
          {/* El nombre entero, sólo para quien no ve la pantalla. Un `aria-label`
              en el propio <p> NO vale: su rol es `paragraph`, que no admite nombre
              accesible, y el lector lo ignoraría — leyendo entonces las letras
              sueltas, que es justo lo que se quiere evitar. */}
          <span className="sr-only">{producto}</span>
          <span aria-hidden="true">
            <PorLetras t={t} texto={producto} ritmo={T.producto} sube={8} />
          </span>
        </p>
      )}
    </div>
  )
}

/**
 * La fila «25 · Años / Evolucionando la Logística».
 *
 * El «25» a la izquierda y las dos líneas a su derecha, como en la firma: es la
 * lectura natural —cifra, unidad, y debajo qué significa— y por eso el original la
 * compone así y no en tres líneas apiladas.
 */
function Veinticinco({ t }: { t: ReturnType<typeof useMotionValue<number>> }) {
  const entrada = useTransform(t, T.entra25, [0, 1], { clamp: true })
  const salida = useTransform(t, T.salida, [1, 0], { clamp: true })

  const opacidad = useTransform(
    [entrada, salida],
    ([e, s]: number[]) => suavizar(e ?? 0) * (s ?? 1),
  )
  // 0,92 → 1: el «25» se posa en vez de aparecer. Sólo `transform`, nunca `width`.
  const escala = useTransform(entrada, (p) => 0.92 + 0.08 * suavizar(p))
  const halo = useTransform(t, T.halo, [0, 0.9], { clamp: true, ease: suavizar })

  return (
    <motion.div
      style={{ opacity: opacidad }}
      className="absolute inset-0 flex items-center justify-center gap-2.5 sm:gap-3"
      aria-hidden="true"
    >
      {/* El halo dorado detrás del «25» (11/10/2026): le da peso al número sin
          pintar un rectángulo — es luz sobre el cristal, del dorado del filete.
          Va dentro de la fila, así que se va con ella al salir. Se centra con
          `inset` y `m-auto`, no con `translate`: `transform` es de motion. */}
      <motion.div
        style={{ opacity: halo }}
        className="pointer-events-none absolute inset-0 m-auto h-36 w-56 rounded-full bg-[radial-gradient(ellipse,rgb(var(--acceso-oro)/0.32)_0%,rgb(var(--acceso-oro)/0)_68%)] sm:h-40 sm:w-60"
      />
      {/* El «25» solo. Es el MISMO vectorial recortado por su `viewBox`: mantener
          un segundo archivo para el número era mantener dos veces la misma marca. */}
      <motion.div style={{ scale: escala }} className="shrink-0">
        <LogoSetieca
          pieza="numero"
          className="h-[62px] w-auto text-[rgb(var(--marco-tinta)/0.95)] sm:h-[82px]"
        />
      </motion.div>
      <div>
        <p className="text-[1.35rem] leading-none font-semibold text-[var(--marca-25-palabra)] sm:text-[1.75rem]">
          <PorLetras t={t} texto={PALABRA} ritmo={T.palabra} sube={10} />
        </p>
        <p className="mt-1 text-[0.7rem] leading-tight font-medium text-[var(--marca-25-frase)] sm:text-[0.8rem]">
          <PorLetras t={t} texto={FRASE} ritmo={T.frase} sube={6} />
        </p>
      </div>
    </motion.div>
  )
}

/**
 * Un texto que se escribe letra a letra.
 *
 * ⚠ **Las letras se agrupan por PALABRA, y hace falta.** Cada letra es un
 * `inline-block` para poder desplazarla, y entre dos `inline-block` el navegador
 * puede partir la línea: «Evolucionando la Logístic / a». Envolviendo cada palabra
 * en un `whitespace-nowrap`, la línea sólo se parte donde tiene que partirse — en
 * los espacios.
 *
 * ⚠ **El bloque va `aria-hidden` desde fuera.** Partir el texto en caracteres es lo
 * que permite el escalonado, pero un lector de pantalla lo silabearía; el nombre
 * accesible lo pone quien llama, con un `alt` o un `aria-label`.
 */
function PorLetras({
  t,
  texto,
  ritmo,
  sube,
}: {
  t: ReturnType<typeof useMotionValue<number>>
  texto: string
  ritmo: { ini: number; paso: number; dur: number }
  sube: number
}) {
  // El desfase va con el índice GLOBAL de la letra, no con su posición dentro de la
  // palabra: si se reiniciara en cada palabra, la frase se escribiría a tirones.
  let indice = -1

  return (
    <>
      {texto.split(" ").map((palabra, p, todas) => {
        const letras = [...palabra].map((letra) => ({ letra, i: ++indice }))
        // El espacio también cuenta para el ritmo — si no, la pausa entre palabras
        // desaparecería y el texto se escribiría más rápido de lo previsto.
        const espacio = p < todas.length - 1 ? ++indice : null

        // La clave lleva el indice GLOBAL de la primera letra, no la posicion de la
        // palabra en el array: las palabras se repiten —«la», «de»— y ese numero es
        // lo unico que las separa sin caer en el indice del map.
        const clave = `${palabra}-${letras[0]?.i ?? espacio ?? p}`

        return (
          <span key={clave} className="inline-block whitespace-nowrap">
            {letras.map(({ letra, i }) => (
              <Letra
                key={i}
                t={t}
                letra={letra}
                ini={ritmo.ini + i * ritmo.paso}
                dur={ritmo.dur}
                sube={sube}
              />
            ))}
            {espacio !== null && (
              <Letra
                t={t}
                letra=" "
                ini={ritmo.ini + espacio * ritmo.paso}
                dur={ritmo.dur}
                sube={sube}
              />
            )}
          </span>
        )
      })}
    </>
  )
}

function Letra({
  t,
  letra,
  ini,
  dur,
  sube,
}: {
  t: ReturnType<typeof useMotionValue<number>>
  letra: string
  ini: number
  dur: number
  sube: number
}) {
  const avance = useTransform(t, [ini, ini + dur], [0, 1], { clamp: true })
  const opacidad = useTransform(avance, suavizar)
  // Sube al aparecer: `y` y `opacity` son transformaciones baratas, y el pequeño
  // salto es lo que hace que la letra parezca posarse en la línea.
  const y = useTransform(avance, (p) => (1 - suavizar(p)) * sube)

  return (
    <motion.span style={{ opacity: opacidad, y }} className="inline-block whitespace-pre">
      {letra}
    </motion.span>
  )
}

/**
 * El logotipo completo, revelado por un barrido diagonal.
 *
 * ⚠ **El barrido va de abajo-derecha a arriba-izquierda**, que es el eje del
 * original —su degradado va de (245,386) a (150,24)— y no una diagonal cualquiera:
 * recorre el trazo del «25» en el sentido en que está dibujado, así que el logotipo
 * se descubre siguiendo su propia forma en vez de que lo destape una cortina.
 */
function Logotipo({ t }: { t: ReturnType<typeof useMotionValue<number>> }) {
  const avance = useTransform(t, [T.logo, T.logo + T.trazo], [0, 1], { clamp: true })
  // Un fundido corto además del barrido: sin él, el primer borde aparece de golpe
  // sobre la lámina vacía y se ve el corte.
  const opacidad = useTransform(t, [T.logo, T.logo + 200], [0, 1], { clamp: true })
  const filete = useTransform(t, T.filete, [0, 1], { clamp: true, ease: suavizar })

  const mascara = useTransform(avance, (p) => {
    const borde = p * (1 + SUAVE)
    return `linear-gradient(345deg, black 0%, black ${pct(borde - SUAVE)}, transparent ${pct(borde)}, transparent 100%)`
  })

  return (
    <motion.div
      style={{ opacity: opacidad, maskImage: mascara, WebkitMaskImage: mascara }}
      className="absolute inset-0 flex items-center justify-center py-2"
    >
      {/* El único `alt` del bloque de marca: es lo que anuncia un lector de
          pantalla, y por eso la fila de la secuencia va `aria-hidden`.

          ⚠ **`layoutId` compartido con la barra lateral**: al entrar, este logotipo
          no desaparece de aquí para reaparecer allí — se encoge y sube hasta la
          cabecera del menú. Ver `lib/transiciones.ts`. */}
      {/* ⚠ **`preserve-aspect` y no el `layout` por omisión.** Con `layoutId`, motion
          interpola la CAJA de un extremo al otro, y aquí las dos cajas no tienen la
          misma proporción: en el acceso es ancha y baja —el logotipo con su descriptor
          debajo— y en el menú es casi cuadrada. Interpolando ancho y alto por separado,
          el contenido se estruja a mitad de camino y el logotipo se ve angosto durante
          el viaje aunque aterrice bien. Con esto, motion escala manteniendo la relación
          de aspecto y sólo compensa la diferencia con posición. */}
      <motion.div
        layoutId={MARCA_SESION}
        transition={VIAJE_LAMINA}
        layout="preserve-aspect"
        className="flex h-full flex-col items-center justify-center gap-1"
      >
        <LogoSetieca
          titulo="SETIECA"
          className="min-h-0 flex-1 text-[rgb(var(--marco-tinta)/0.95)]"
        />
        {/* El descriptor, en TEXTO: el vectorial de la marca no lo trae, y como
            texto se selecciona, lo lee un lector de pantalla y se cambia en un
            sitio el día que cambie la razón social. */}
        <span className="font-marca text-[0.6rem] tracking-[0.08em] text-[rgb(var(--marco-tinta)/0.7)] sm:text-[0.7rem]">
          Agentes Aduanales, c.a.
        </span>
      </motion.div>
      {/* El filete dorado de la firma corporativa (11/10/2026), trazado desde el
          centro bajo el descriptor. ⚠ **Va FUERA del `layoutId`**: es de esta
          lámina, no de la marca, y no tiene que viajar al menú. Los cuatro tramos
          son los del filete impreso — dos paradas se verían como una franja beige. */}
      <motion.span
        aria-hidden="true"
        style={{ scaleX: filete }}
        className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto h-0.5 w-28 origin-center rounded-full bg-[linear-gradient(90deg,rgb(var(--acceso-oro-oscuro)),rgb(var(--acceso-oro-claro))_30%,rgb(var(--acceso-oro))_55%,rgb(var(--acceso-oro-claro))_80%,rgb(var(--acceso-oro-oscuro)))]"
      />
    </motion.div>
  )
}

/** Suavizado de salida: entra rápido y se asienta, en vez de llegar de golpe. */
function suavizar(p: number): number {
  return 1 - (1 - p) ** 3
}

/** Fracción a porcentaje, acotada: un `120%` en una parada de gradiente no vale. */
function pct(v: number): string {
  return `${Math.max(0, Math.min(1, v)) * 100}%`
}
